import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';
import { BaseService } from 'src/app/core/services/base.httpService';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import { IManifestInformationData } from '../model/manifest.model';
import {
  HttpSignatures,
  ModalPopupMessages,
  popupWidth,
  StatusCodes,
} from 'src/app/shared/utility/constants';
import { MessagePopupComponent } from 'src/app/shared/components/message-popup/message-popup.component';
import { SnakbarService } from 'src/app/shared/services/snakbar.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { CommonService } from 'src/app/shared/services/common.service';

@Injectable({
  providedIn: 'root',
})
export class ManifestService {
  public isManifestPageSummary = new BehaviorSubject<string>('');
  public isPublishManifestApiCall = new BehaviorSubject<boolean>(false);
  public isManSummaryApiData = new BehaviorSubject<any>(false);
  private readonly errorHandler = handleErrorMessage();

  constructor(
    public baseService: BaseService,
    private readonly snakbarService: SnakbarService,
    private readonly modalService: ModalService,
    private readonly commonService: CommonService
  ) {}

  /* set data using subject */
  public isManifestSummaryPageInfo(data: string) {
    this.isManifestPageSummary.next(data);
  }

  public getBagDataByExt(
    requestType: string,
    data: any,
    originId: number,
    destinationId: number,
    /** When false, subscriber handles errors (e.g. Scan Bags popup toasts + focus). */
    handleErrorsGlobally = true
  ): Observable<any> {
    const url = `${URLConstants.getBagsForManifest}/${requestType}/${data}/${originId}/${destinationId}`;
    const request = this.baseService.initiateApiRequest('GET', url, '', 'json');
    return handleErrorsGlobally ? request.pipe(this.errorHandler()) : request;
  }

  public createManifest(data: IManifestInformationData): Observable<any> {
    const url = URLConstants.createManifest;
    return this.baseService
      .initiateApiRequest('PUT', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public updateManifest(data: IManifestInformationData): Observable<any> {
    const url = URLConstants.updateManifest;
    return this.baseService
      .initiateApiRequest('POST', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public checkMawbForManifest(data: any) {
    const url = URLConstants.checkMawbExists;
    return this.baseService.initiateApiRequest('POST', url, data, 'json');
  }

  public checkFlightForManifest(data: any) {
    const url = URLConstants.checkFlightExists;
    return this.baseService.initiateApiRequest('POST', url, data, 'json');
  }

  public getSearchData(event: any): Observable<any> {
    const apiUrl = URLConstants.getManifestSearchList;
    return this.baseService
      .initiateApiRequest('POST', apiUrl, event, '', 'json')
      .pipe(this.errorHandler());
  }

  public deleteManifests(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteManifest,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public deleteManifestBags(event: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.deleteManifestBags,
        event,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public linkBagToManifest(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.createManifestBag,
      data,
      '',
      'json'
    );
  }

  public checkManifestExistence(manifestId: string): Observable<any> {
    const url = URLConstants.getManifestById + manifestId;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public getManifestBulkEditUniqueData(manifestIds: string): Observable<any> {
    const url = URLConstants.uniqueDataBulkEdit + manifestIds;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public updateBulkManifest(data: IManifestInformationData): Observable<any> {
    const url = URLConstants.updateBulkManifest;
    return this.baseService
      .initiateApiRequest('POST', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public advanceManifest(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.advanceManifestInformation,
      data,
      'json'
    );
  }

  public CheckPublishManifest(data: boolean) {
    this.isPublishManifestApiCall.next(data);
  }

  public publishManifestData(data: any) {
    const apiUrl = URLConstants.publishManifest;
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      apiUrl,
      data,
      'json'
    );
  }

  public InitiatePublishManifestAPI(requestPayload: any) {
    const apiPayload = this.buildPublishPayload(requestPayload);

    // Intentionally not piping errorHandler: blocked-shipment publish failures must
    // surface as a modal popup (not a toast) to preserve the pre-migration UX.
    this.publishManifestData(apiPayload)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (response.statusCode == StatusCodes.Success) {
            const manifestCount = requestPayload.manifestIds?.length ?? 0;
            const createTicketMessage =
              ModalPopupMessages.getPublishManifestSuccessWithTicketMessage(
                manifestCount
              );
            const successMessage = requestPayload.isCreateTicket
              ? createTicketMessage
              : response.message;
            this.snakbarService.openCustomisedSnackBar(
              successMessage,
              ModalPopupMessages.success
            );
            this.CheckPublishManifest(true);
            const transformedData = {
              requestType: 'manifest',
              isFromId: requestPayload.manifestIds[0],
            };
            this.commonService.triggerLoadMoreEvents(transformedData);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err?.error?.code === 'ASSOCIATION_EXISTS') {
            this.modalService.openPopup(
              {
                message: err.error.message,
                isFooterRequired: false,
              },
              ModalPopupMessages.Warning,
              MessagePopupComponent,
              popupWidth.deletePopup
            );
            return;
          }
          const errMessage =
            err?.error?.message ?? 'Failed to publish manifest';
          this.snakbarService.openCustomisedSnackBar(errMessage, 'error');
        },
      });
  }

  private buildPublishPayload(requestPayload: any): any {
    const payload: any = {
      manifestIds: requestPayload.manifestIds,
      isCreateTicket: requestPayload.isCreateTicket ?? true,
    };
    const emailPayload = requestPayload.manifestEmailPayload;
    if (!emailPayload) {
      return payload;
    }
    payload.isSendEmail = emailPayload.isSendEmail;
    payload.sendToMe = emailPayload.sendToMe;
    payload.additionalEmails = emailPayload.additionalEmails;
    payload.stationFormatId = emailPayload.stationFormatId;
    payload.fileFormat = emailPayload.fileFormat;
    payload.emailSubject = emailPayload.emailSubject;
    payload.attachmentFileNames = emailPayload.attachmentFileNames ?? [];
    return payload;
  }
  public bulkMawbValidate(selectedData: any) {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.bulkMAWBValidate,
      selectedData,
      'json'
    );
  }

  public getManifestByManifestId(manifestId: number): Observable<any> {
    const url = `${URLConstants.getManifestByManIds}${manifestId}`;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  /** PAC-24667: manifests (with `bags[].shipmentDetails`) for MAWB nested print. */
  public getManifestsByIds(manifestIds: number[]): Observable<any> {
    const url = `${URLConstants.getManifestByManIds}${manifestIds.join(',')}`;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public isManifestSummaryApiData(data: boolean) {
    this.isManSummaryApiData.next(data);
  }
  public downloadManifestHeldReport(fetchParams: any): Observable<any> {
    const url = URLConstants.downloadHeldReportUrl + fetchParams;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }
  public getOriginNameById(id: number, list: any[]): string {
    return list.find(o => o.id === id)?.name ?? '';
  }

  public getManifestPublishFormats(agentId: number): Observable<any> {
    const url = `${URLConstants.getManifestPublishFormats}?agentId=${agentId}`;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public manifestExistsForBulkManifesting(data: string) {
    const url = `${URLConstants.bulkManifestNumberValidate}/${data}`;
    return this.baseService
      .initiateApiRequest('GET', url, data, 'json')
      .pipe(this.errorHandler());
  }

  /* To create bulk manifest **/
  public createManifestForBulkManifesting(manifestData: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.createBulkManifest,
        manifestData,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }
}
